<template>
  <div>
    <div
      ref="backdrop"
      v-show="isOpen"
      class="bulk-modal-backdrop"
      @click.self="close"
    >
      <div class="bulk-modal">
        <div class="bulk-modal-header">
          <span style="font-weight:bold">Bulk Add from File</span>
          <button @click="close" class="bulk-modal-close">×</button>
        </div>
        <div class="bulk-modal-body">
          <!-- Step 1: Upload -->
          <div v-if="step === 'upload'">
            <p style="color:#6b7280;margin-bottom:12px;font-size:0.875rem">
              Upload a <strong>.txt</strong> or <strong>.csv</strong> file with
              columns: <code>nkr_code</code>, <code>description</code>,
              <code>qty</code> (tab or comma separated).
            </p>
            <input
              type="file"
              accept=".txt,.csv,.tsv"
              @change="onFileChange"
              style="display:block;width:100%;border:1px solid #d1d5db;border-radius:6px;padding:8px;font-size:0.875rem"
            />
            <p
              v-if="parseError"
              style="color:#ef4444;margin-top:8px;font-size:0.875rem"
            >
              {{ parseError }}
            </p>
          </div>

          <!-- Step 2: Review -->
          <div v-else-if="step === 'review'">
            <div
              style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px"
            >
              <span style="font-size:0.875rem;color:#6b7280">
                {{ rows.length }} products ·
                <span style="color:#16a34a">{{ addedCount }} added</span>
              </span>
              <div style="display:flex;gap:8px">
                <button
                  class="btn-dark-button"
                  @click="loadAll"
                  :disabled="loadingAll"
                >
                  {{ loadingAll ? "Loading..." : "Reload All" }}
                </button>
                <button
                  class="btn-info-button"
                  @click="addAll"
                  :disabled="!hasAnyToAdd"
                >
                  Add All Ready
                </button>
              </div>
            </div>

            <div class="bulk-table-wrap">
              <table style="width:100%;font-size:0.75rem">
                <thead>
                  <tr style="background:#f3f4f6;text-align:left">
                    <th style="padding:8px">Code</th>
                    <th style="padding:8px">Description</th>
                    <th style="padding:8px;text-align:center">Qty</th>
                    <th style="padding:8px">Option</th>
                    <th style="padding:8px;text-align:center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in rows"
                    :key="row.nkrCode"
                    style="border-bottom:1px solid #f3f4f6"
                    :style="row.status === 'added' ? 'background:#f0fdf4' : ''"
                  >
                    <td
                      style="padding:8px;font-family:monospace;font-weight:600"
                    >
                      {{ row.nkrCode }}
                    </td>
                    <td
                      style="padding:8px;color:#6b7280;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
                    >
                      <span
                        v-if="row.loading"
                        style="color:#9ca3af;font-style:italic"
                        >Loading...</span
                      >
                      <span v-else-if="row.error" style="color:#ef4444">{{
                        row.error
                      }}</span>
                      <span v-else>{{
                        row.productData
                          ? row.productData.customTitle ||
                            row.productData.originalTitle
                          : row.description
                      }}</span>
                    </td>
                    <td style="padding:8px;text-align:center">
                      <input
                        type="number"
                        v-model.number="row.quantity"
                        min="1"
                        style="width:56px;border:1px solid #d1d5db;border-radius:4px;padding:2px 4px;text-align:center"
                      />
                    </td>
                    <td style="padding:8px">
                      <span v-if="row.loading" style="color:#9ca3af">—</span>
                      <select
                        v-else-if="row.options && row.options.length > 0"
                        v-model="row.selectedOption"
                        style="border:1px solid #d1d5db;border-radius:4px;padding:2px 4px;width:100%;max-width:260px"
                      >
                        <option
                          v-for="opt in row.options"
                          :key="opt.productId"
                          :value="opt"
                        >
                          {{ opt.warehouse }} · {{ opt.line }} ·
                          {{ opt[priceKey] | currencyFormatted(true) }} ·
                          {{ opt.stock }} stk
                        </option>
                      </select>
                      <span
                        v-else-if="
                          row.productData &&
                            (!row.options || row.options.length === 0)
                        "
                        style="color:#f97316"
                      >
                        No stock for {{ departmentCode }}
                      </span>
                      <span v-else style="color:#d1d5db">—</span>
                    </td>
                    <td style="padding:8px;text-align:center">
                      <span
                        v-if="row.status === 'added'"
                        style="color:#16a34a;font-weight:600"
                        >Added</span
                      >
                      <button
                        v-else-if="row.selectedOption"
                        @click="addRow(row)"
                        style="background:#2563eb;color:white;padding:4px 8px;border-radius:4px;font-size:0.75rem"
                      >
                        Add
                      </button>
                      <span v-else style="color:#e5e7eb">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProducts } from "@/api/store/ProductsService.js";

let camelize = t => {
  const words = t.split(/[-_]/g);
  return (
    words[0].toLowerCase() +
    words
      .slice(1)
      .map(w => w[0].toUpperCase() + w.toLowerCase().slice(1))
      .join("")
  );
};

export default {
  name: "BulkOrderUpload",
  props: {
    departmentCode: { type: String, default: null },
    priceLevel: { type: String, default: "online" }
  },
  data() {
    return {
      isOpen: false,
      step: "upload",
      rows: [],
      parseError: "",
      loadingAll: false
    };
  },
  computed: {
    priceKey() {
      return camelize(this.priceLevel || "online");
    },
    hasAnyToAdd() {
      return this.rows.some(r => r.selectedOption && r.status !== "added");
    },
    addedCount() {
      return this.rows.filter(r => r.status === "added").length;
    }
  },
  mounted() {
    document.body.appendChild(this.$refs.backdrop);
  },
  beforeDestroy() {
    const el = this.$refs.backdrop;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    open() {
      this.$emit("open");
      this.isOpen = true;
      this.step = "upload";
      this.rows = [];
      this.parseError = "";
    },
    close() {
      this.isOpen = false;
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => this.parseFile(e.target.result);
      reader.readAsText(file);
    },
    parseFile(text) {
      this.parseError = "";
      const lines = text
        .split("\n")
        .map(l => l.trim())
        .filter(l => l.length > 0);

      if (lines.length === 0) {
        this.parseError = "File is empty.";
        return;
      }

      const sep = lines[0].includes("\t") ? "\t" : ",";
      const headers = lines[0].split(sep).map(h => h.trim().toLowerCase());
      const codeIdx = headers.findIndex(h =>
        ["nkr_code", "code", "nkrcode"].includes(h)
      );

      if (codeIdx === -1) {
        this.parseError =
          "Could not find nkr_code column. Expected: nkr_code, description, qty";
        return;
      }

      const descIdx = headers.findIndex(h =>
        ["description", "desc"].includes(h)
      );
      const qtyIdx = headers.findIndex(h => ["qty", "quantity"].includes(h));
      const seen = new Set();
      const rows = [];

      for (const line of lines.slice(1)) {
        const cols = line.split(sep).map(c => c.trim().replace(/^"|"$/g, ""));
        const nkrCode = cols[codeIdx] || "";
        if (!nkrCode || seen.has(nkrCode)) continue;
        seen.add(nkrCode);
        rows.push({
          nkrCode,
          description: descIdx >= 0 ? cols[descIdx] : "",
          quantity: qtyIdx >= 0 ? parseInt(cols[qtyIdx]) || 1 : 1,
          productData: null,
          options: [],
          allOptions: [],
          selectedOption: null,
          loading: false,
          error: "",
          status: "pending"
        });
      }

      if (rows.length === 0) {
        this.parseError = "No valid rows found in file.";
        return;
      }

      this.rows = rows;
      this.step = "review";
      this.loadAll();
    },
    async loadAll() {
      this.loadingAll = true;
      const BATCH = 10;
      for (let i = 0; i < this.rows.length; i += BATCH) {
        await Promise.all(
          this.rows.slice(i, i + BATCH).map(row => this.loadProduct(row))
        );
      }
      this.loadingAll = false;
    },
    async loadProduct(row) {
      row.loading = true;
      row.error = "";
      row.productData = null;
      row.options = [];
      row.allOptions = [];
      row.selectedOption = null;

      try {
        const res = await getProducts({
          storeEnabled: "enabled",
          page: 1,
          limitPerPage: 10,
          orderCol: "nkr_code",
          orderDir: "asc",
          search: row.nkrCode,
          requestPrice: true,
          priceLevel: this.priceLevel || "online",
          departmentCode: this.departmentCode
        });
        const products = res.products || [];
        const product =
          products.find(p => p.nkrCode === row.nkrCode) || products[0];

        if (!product) {
          row.error = "Not found";
          row.loading = false;
          return;
        }

        row.productData = product;
        row.allOptions = product.pricesAndStock || [];
        row.options = row.allOptions.filter(
          opt =>
            opt.canSell === true &&
            opt.departamentoCodigo === this.departmentCode
        );

        if (row.options.length > 0) {
          const pool = row.options.filter(
            o => o.warehouse !== "FBA" && o.line !== "OB"
          );
          const candidates = pool.length > 0 ? pool : row.options;
          const key = this.priceKey;
          row.selectedOption = candidates.reduce((best, cur) => {
            const cp = parseFloat(cur[key]) || 0;
            const bp = parseFloat(best[key]) || 0;
            if (cp !== bp) return cp < bp ? cur : best;
            return (parseInt(cur.warehousePriority) || 0) >
              (parseInt(best.warehousePriority) || 0)
              ? cur
              : best;
          });
        }
      } catch {
        row.error = "Error loading";
      }

      row.loading = false;
    },
    buildProduct(row) {
      const opt = row.selectedOption;
      return {
        productId: opt.productId,
        nkrCode: row.nkrCode,
        description: row.productData
          ? row.productData.customTitle || row.productData.originalTitle
          : row.description,
        brand: row.productData ? row.productData.brand : "",
        image: row.productData ? row.productData.image : null,
        pack: row.productData ? row.productData.pack : "",
        core: row.productData ? row.productData.core : null,
        price: opt[this.priceKey],
        quantity: row.quantity,
        line: opt.line,
        warehouseId: opt.warehouseId,
        warehouseCode: opt.warehouse,
        departmentCode: opt.departamentoCodigo,
        custom: false,
        isTransfer: false,
        pricesAndStock: row.allOptions
      };
    },
    addRow(row) {
      if (!row.selectedOption) return;
      this.$emit("addProduct", this.buildProduct(row));
      row.status = "added";
    },
    addAll() {
      this.rows.forEach(row => {
        if (row.selectedOption && row.status !== "added") {
          this.addRow(row);
        }
      });
    }
  }
};
</script>

<style lang="scss">
.bulk-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.bulk-modal {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.bulk-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;
}

.bulk-modal-close {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e5e7eb;
  font-weight: bold;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #d1d5db;
  }
}

.bulk-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.bulk-table-wrap {
  max-height: 55vh;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-top: 8px;
}
</style>
